import axiosClient, { getValidAccessToken } from 'utils/axios';
import { BASE_URL } from 'utils/baseUrlProvider';
import { extractSummaryText, getSummaryBlocksFromPayload } from 'utils/aiSummaryHelper';
import {
  SUMMARY_WEBSOCKET_PATH,
  buildWebSocketUrl,
  buildPatientSummaryStartMessage,
  buildIPDSummaryStartMessage,
  streamAiSummaryWebSocket
} from 'utils/aiWebSocket';

const isAiSummaryWebSocketEnabled = () =>
  process.env.REACT_APP_AI_SUMMARY_WS_ENABLED !== 'false';

const getSummaryWebSocketUrl = () => buildWebSocketUrl(SUMMARY_WEBSOCKET_PATH);

/**
 * GET ml/patient-summary/<patientId>/
 */
export async function fetchPatientSummary(patientId, options = {}) {
  const includeInterpretation = options.includeInterpretation === true;
  const url = `${BASE_URL}/ml/patient-summary/${patientId}/?include_interpretation=${includeInterpretation}`;
  const response = await axiosClient.get(url, {
    signal: options.signal
  });
  return response.data;
}

/**
 * GET ml/ipd-discharge-summary/<admissionId>/
 */
export async function fetchIPDDischargeSummary(admissionId, options = {}) {
  const includeInterpretation = options.includeInterpretation === true;
  const url = `${BASE_URL}/ml/ipd-discharge-summary/${admissionId}/?include_interpretation=${includeInterpretation}`;
  const response = await axiosClient.get(url, {
    signal: options.signal
  });
  return response.data;
}

const fetchWithHttpReveal = async ({ fetcher, handlers }) => {
  const { onUpdate, onStatus, onFinalPayload, signal } = handlers;
  onStatus?.('Fetching summary from server');

  const response = await fetcher({ signal });
  onFinalPayload?.(response);

  const summary = extractSummaryText(response);

  if (!summary) {
    throw new Error('Empty summary response');
  }
  onUpdate?.(summary);

  return {
    text: summary,
    summaryBlocks: getSummaryBlocksFromPayload(response)
  };
};

const streamSummaryWithFallback = async ({
  startMessage,
  httpFetcher,
  handlers
}) => {
  const { onUpdate, onStatus, onFinalPayload, signal } = handlers;
  let summaryBlocks = null;

  const captureFinalPayload = (payload) => {
    if (payload) {
      const blocks = getSummaryBlocksFromPayload(payload);
      if (blocks?.length) {
        summaryBlocks = blocks;
      }
    }
    onFinalPayload?.(payload);
  };

  if (isAiSummaryWebSocketEnabled()) {
    let usedHttpFallback = false;

    try {
      // An expired token makes the server reject the socket, which would push
      // every request onto the slow HTTP fallback.
      await getValidAccessToken();
      const streamedText = await streamAiSummaryWebSocket({
        wsUrl: getSummaryWebSocketUrl(),
        startMessage,
        signal,
        onStatus,
        onFinalPayload: captureFinalPayload,
        onChunk: (_chunk, fullText) => onUpdate?.(fullText),
        onTransportFallback: () => {
          usedHttpFallback = true;
          if (process.env.NODE_ENV === 'development') {
            console.info('[ai-summary] Falling back to HTTP after WebSocket failure');
          }
        }
      });

      if (streamedText) {
        return {
          text: streamedText,
          summaryBlocks
        };
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        throw error;
      }

      if (!usedHttpFallback && process.env.NODE_ENV === 'development') {
        console.info('[ai-summary] WebSocket error, using HTTP fallback:', error.message);
      }
    }
  }

  return fetchWithHttpReveal({
    fetcher: httpFetcher,
    handlers: { onUpdate, onStatus, onFinalPayload: captureFinalPayload, signal }
  });
};

export async function fetchPatientSummaryWithReveal(patientId, handlers = {}) {
  const { includeInterpretation = false, ...streamHandlers } = handlers;

  return streamSummaryWithFallback({
    startMessage: buildPatientSummaryStartMessage(patientId, includeInterpretation),
    httpFetcher: ({ signal }) =>
      fetchPatientSummary(patientId, { signal, includeInterpretation }),
    handlers: streamHandlers
  });
}

export async function fetchIPDDischargeSummaryWithReveal(admissionId, handlers = {}) {
  const { includeInterpretation = false, ...streamHandlers } = handlers;

  return streamSummaryWithFallback({
    startMessage: buildIPDSummaryStartMessage(admissionId, includeInterpretation),
    httpFetcher: ({ signal }) =>
      fetchIPDDischargeSummary(admissionId, { signal, includeInterpretation }),
    handlers: streamHandlers
  });
}

export { extractSummaryText, getSummaryBlocksFromPayload };
